import BaseTool from './BaseTool.js';
import Transform from '../../math/Transform.js';
import Material from '../../materials/Material.js';

/**
 * Tool for moving cuboids to new positions
 * Uses delta-based movement similar to camera panning for accurate control
 */
export default class MoveTool extends BaseTool {
  constructor(editor) {
    super(editor);

    // Drag state
    this.draggingCuboid = null;
    this.originalPosition = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragFace = null; // Which face was grabbed - determines movement axis
  }

  get name() {
    return 'move';
  }

  /**
   * Handle mouse down - start dragging if clicking a cuboid face
   * @param {MouseEvent} event
   */
  onMouseDown(event) {
    if (event.button !== 0) return; // Left click only

    const faceEl = event.target.closest('.face');
    if (!faceEl || faceEl.closest('.ghost-preview')) return;

    const cuboid = this.getCuboidFromElement(faceEl);
    if (!cuboid) return;

    const face = this.getFaceType(faceEl);
    if (!face) return;

    // Start dragging
    this.draggingCuboid = cuboid;
    this.dragFace = face;
    this.originalPosition = {
      x: cuboid.x,
      y: cuboid.y,
      z: cuboid.z
    };
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.isDragging = true;

    // Show ghost at original position
    this.ghostPreview.show(
      cuboid.x, cuboid.y, cuboid.z,
      cuboid.width, cuboid.height, cuboid.depth,
      true
    );
  }

  /**
   * Handle mouse move - update ghost preview position
   * @param {MouseEvent} event
   */
  onMouseMove(event) {
    if (!this.isDragging || !this.draggingCuboid) return;

    this.updateGhostPosition(event);
  }

  /**
   * Handle mouse up - finalize move if valid
   * @param {MouseEvent} event
   */
  onMouseUp(event) {
    if (!this.isDragging || !this.draggingCuboid) {
      this.cancelDrag();
      return;
    }

    const newPos = this.calculateNewPosition(event);
    const cuboid = this.draggingCuboid;
    const key = cuboid.getKey();

    // Check if position actually changed
    const hasChanged = (
      newPos.x !== this.originalPosition.x ||
      newPos.y !== this.originalPosition.y ||
      newPos.z !== this.originalPosition.z
    );

    // Check if new position is valid
    const isValid = hasChanged && this.collisionManager.isValidPlacement(
      newPos.x, newPos.y, newPos.z,
      cuboid.width, cuboid.height, cuboid.depth,
      key // Exclude self
    );

    if (isValid) {
      // Snapshot impostors sitting on this cuboid before moving
      const impostorsOnCuboid = this.world.getImpostorsOnCuboid(cuboid);

      // Save materials
      const materials = {};
      ['top', 'bottom', 'front', 'back', 'left', 'right'].forEach(face => {
        materials[face] = cuboid.materials[face];
      });

      // Save group membership for each face before removal
      const faceGroups = {};
      const groupManager = this.world.materialGroupManager;
      if (groupManager) {
        ['top', 'bottom', 'front', 'back', 'left', 'right'].forEach(face => {
          const group = groupManager.getGroupForFace(key, face);
          if (group) {
            faceGroups[face] = group.id;
          }
        });
      }

      // Remove from old position
      this.world.removeCuboid(
        this.originalPosition.x,
        this.originalPosition.y,
        this.originalPosition.z,
        cuboid.width,
        cuboid.height,
        cuboid.depth
      );

      // Translate impostor positions BEFORE addCuboid validates them
      if (impostorsOnCuboid.length > 0) {
        const dx = newPos.x - this.originalPosition.x;
        const dy = newPos.y - this.originalPosition.y;
        const dz = newPos.z - this.originalPosition.z;
        for (const imp of impostorsOnCuboid) {
          imp.x += dx;
          imp.y += dy;
          imp.z += dz;
        }
      }

      // Add at new position (validateAllImpostors runs inside, checking edge margins,
      // wall intersections, and floor validity for ALL impostors in the world)
      this.world.addCuboid(
        newPos.x,
        newPos.y,
        newPos.z,
        cuboid.width,
        cuboid.height,
        cuboid.depth,
        materials
      );

      // Restore group membership with new cuboid key
      const newKey = `${newPos.x},${newPos.y},${newPos.z},${cuboid.width},${cuboid.height},${cuboid.depth}`;
      const restoredGroups = new Set();
      if (groupManager && Object.keys(faceGroups).length > 0) {
        Object.entries(faceGroups).forEach(([face, groupId]) => {
          groupManager.assignFacesToGroup(groupId, [{ cuboidKey: newKey, face }]);
          restoredGroups.add(groupId);
        });

        // Re-apply materials for groups that were restored (fixes triplanar UV for new position)
        restoredGroups.forEach(groupId => {
          groupManager.applyGroupMaterial(groupId);
        });
      }

      // Update face selection to use new key
      this.updateSelectionKey(key, newKey);

      // Notify UI if impostors were on the affected cuboid
      if (impostorsOnCuboid.length > 0) {
        window.dispatchEvent(new CustomEvent('impostors-changed'));
      }

      // Record undo action
      if (this.editor.undoManager) {
        const oldPos = { ...this.originalPosition };
        const nPos = { x: newPos.x, y: newPos.y, z: newPos.z };
        const w = cuboid.width, h = cuboid.height, d = cuboid.depth;
        const oldK = key;
        const newK = newKey;
        // Snapshot materials (deep copy)
        const matSnap = {};
        for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
          if (materials[face]) matSnap[face] = materials[face].toJSON();
        }
        const groupSnap = { ...faceGroups };
        // Snapshot impostor deltas
        const impMoves = impostorsOnCuboid.map(imp => ({
          id: imp.id,
          dx: nPos.x - oldPos.x,
          dy: nPos.y - oldPos.y,
          dz: nPos.z - oldPos.z
        }));
        const world = this.world;
        this.editor.undoManager.push({
          description: 'Move cuboid',
          undo: () => {
            const gm = world.materialGroupManager;
            if (gm) {
              for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
                const group = gm.getGroupForFace(newK, face);
                if (group) gm.removeFacesFromGroup(group.id, [{ cuboidKey: newK, face }]);
              }
            }
            world.removeCuboid(nPos.x, nPos.y, nPos.z, w, h, d);
            for (const m of impMoves) {
              const imp = world.impostors.get(m.id);
              if (imp) { imp.x -= m.dx; imp.y -= m.dy; imp.z -= m.dz; }
            }
            const restored = {};
            for (const [face, data] of Object.entries(matSnap)) restored[face] = Material.fromJSON(data);
            world.addCuboid(oldPos.x, oldPos.y, oldPos.z, w, h, d, restored);
            if (gm) {
              const groups = new Set();
              for (const [face, groupId] of Object.entries(groupSnap)) {
                gm.assignFacesToGroup(groupId, [{ cuboidKey: oldK, face }]);
                groups.add(groupId);
              }
              groups.forEach(gid => gm.applyGroupMaterial(gid));
            }
            world.update();
          },
          redo: () => {
            const gm = world.materialGroupManager;
            if (gm) {
              for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
                const group = gm.getGroupForFace(oldK, face);
                if (group) gm.removeFacesFromGroup(group.id, [{ cuboidKey: oldK, face }]);
              }
            }
            world.removeCuboid(oldPos.x, oldPos.y, oldPos.z, w, h, d);
            for (const m of impMoves) {
              const imp = world.impostors.get(m.id);
              if (imp) { imp.x += m.dx; imp.y += m.dy; imp.z += m.dz; }
            }
            const restored = {};
            for (const [face, data] of Object.entries(matSnap)) restored[face] = Material.fromJSON(data);
            world.addCuboid(nPos.x, nPos.y, nPos.z, w, h, d, restored);
            if (gm) {
              const groups = new Set();
              for (const [face, groupId] of Object.entries(groupSnap)) {
                gm.assignFacesToGroup(groupId, [{ cuboidKey: newK, face }]);
                groups.add(groupId);
              }
              groups.forEach(gid => gm.applyGroupMaterial(gid));
            }
            world.update();
          }
        });
      }

      // Update the world
      this.world.update();
    }

    this.cancelDrag();
  }

  /**
   * Calculate new position based on the face grabbed
   * Only moves along the axis perpendicular to the grabbed face
   * @param {MouseEvent} event
   * @return {Object} Position {x, y, z}
   */
  calculateNewPosition(event) {
    // Get screen delta
    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;

    // Start with original position
    let newX = this.originalPosition.x;
    let newY = this.originalPosition.y;
    let newZ = this.originalPosition.z;

    // For top/bottom faces, move vertically (Y axis)
    if (this.dragFace === 'top' || this.dragFace === 'bottom') {
      // Pixels per grid unit for Y axis (vertical on screen)
      const pixelsPerUnitY = this.camera.CUBE_SIZE * this.camera.zoomLevel;
      const delta = Math.round(-dy / pixelsPerUnitY);
      newY = this.originalPosition.y + delta;
      return { x: newX, y: newY, z: newZ };
    }

    // For side faces, calculate screen direction of face normal and project mouse onto it
    const angleRad = this.camera.angle * Math.PI / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const yScale = Transform.calculateYScale(this.camera.skewAngle);

    let screenNormalX, screenNormalY;
    let axis; // 'x' or 'z'

    // Face determines which axis to constrain movement to
    // Drag direction determines which way to move (no direction multiplier)
    switch (this.dragFace) {
      case 'right':
      case 'left':
        // X axis movement - screen direction of +X
        screenNormalX = cos;
        screenNormalY = sin * yScale;
        axis = 'x';
        break;
      case 'back':
      case 'front':
        // Z axis movement - screen direction of +Z
        screenNormalX = sin;
        screenNormalY = -cos * yScale;
        axis = 'z';
        break;
      default:
        return { x: newX, y: newY, z: newZ };
    }

    // Calculate how many pixels one grid unit appears as on screen
    const len = Math.sqrt(screenNormalX * screenNormalX + screenNormalY * screenNormalY);
    if (len < 0.001) return { x: newX, y: newY, z: newZ };

    // Pixels per grid unit along this axis
    const pixelsPerUnit = len * this.camera.CUBE_SIZE * this.camera.zoomLevel;

    // Normalize for direction only
    const dirX = screenNormalX / len;
    const dirY = screenNormalY / len;

    // Project mouse movement onto screen axis direction
    const projection = dx * dirX + dy * dirY;

    // Convert pixel movement to grid units (1:1 feel)
    const delta = Math.round(projection / pixelsPerUnit);

    // Apply delta to the appropriate axis
    if (axis === 'x') {
      newX = this.originalPosition.x + delta;
    } else {
      newZ = this.originalPosition.z + delta;
    }

    return { x: newX, y: newY, z: newZ };
  }

  /**
   * Update ghost preview to show new position
   * @param {MouseEvent} event
   */
  updateGhostPosition(event) {
    if (!this.draggingCuboid) return;

    const newPos = this.calculateNewPosition(event);
    const cuboid = this.draggingCuboid;
    const key = cuboid.getKey();

    // Check if position changed
    const hasChanged = (
      newPos.x !== this.originalPosition.x ||
      newPos.y !== this.originalPosition.y ||
      newPos.z !== this.originalPosition.z
    );

    // Check validity
    const isValid = !hasChanged || this.collisionManager.isValidPlacement(
      newPos.x, newPos.y, newPos.z,
      cuboid.width, cuboid.height, cuboid.depth,
      key
    );

    // Show ghost at the exact grid position
    this.ghostPreview.show(
      newPos.x,
      newPos.y,
      newPos.z,
      cuboid.width,
      cuboid.height,
      cuboid.depth,
      isValid
    );
  }

  /**
   * Cancel the current drag operation
   */
  cancelDrag() {
    this.draggingCuboid = null;
    this.originalPosition = null;
    this.dragFace = null;
    this.isDragging = false;
    this.ghostPreview.hide();
  }

  deactivate() {
    this.cancelDrag();
    super.deactivate();
  }
}
